import {
	ApiError,
	type PortalLedgerDisputeResult,
	type StatementLine,
	type StatementLineDisputeReason,
} from "@khazzin/api-client";
import { type TicketFile, TicketFiles } from "@khazzin/ui/patterns";
import { Button, Dialog, Field, Select, Textarea } from "@khazzin/ui/primitives";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { useStoreScope } from "../../app/shell/scope";
import { ticketsKey } from "../tickets/tickets";
import { financeKey } from "./FinancePage";

/**
 * Dispute a statement line (EP10-S09, DA-10-09): a reason, a description and
 * files. The server flags the line and opens its `billing_dispute` ticket in
 * one transaction; the dialog then links to the ticket, and the line shows
 * Disputed.
 */

export const DISPUTE_REASONS: StatementLineDisputeReason[] = [
	"wrong_amount",
	"not_our_order",
	"duplicate_charge",
	"service_not_provided",
	"other",
];

export function DisputeDialog({ line, onClose }: { line: StatementLine; onClose: () => void }) {
	const { t } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const { store } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const [reason, setReason] = useState<StatementLineDisputeReason>("wrong_amount");
	const [description, setDescription] = useState("");
	const [files, setFiles] = useState<TicketFile[]>([]);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<{ message: string; field?: string } | null>(null);
	const [done, setDone] = useState<PortalLedgerDisputeResult | null>(null);

	const submit = () => {
		setSending(true);
		setError(null);
		api
			.disputePortalLedgerEntry(line.ledgerEntryId, {
				reason,
				description: description.trim(),
				...(files.length > 0 ? { attachmentIds: files.map((f) => f.id) } : {}),
			})
			.then(
				(result) => {
					setDone(result);
					void queries.invalidateQueries({ queryKey: financeKey(storeId) });
					void queries.invalidateQueries({ queryKey: ticketsKey(storeId) });
				},
				(cause: unknown) =>
					setError(
						cause instanceof ApiError
							? { message: cause.message, ...(cause.field ? { field: cause.field } : {}) }
							: { message: t("portal.finance.dispute.failed") },
					),
			)
			.finally(() => setSending(false));
	};

	return (
		<Dialog
			open
			onOpenChange={(open) => !open && !sending && onClose()}
			title={t("portal.finance.dispute.title")}
			description={line.description}
			closeLabel={t("ui.actions.close")}
			footer={
				done !== null ? (
					<Button variant="primary" onClick={onClose}>
						{t("ui.actions.close")}
					</Button>
				) : (
					<>
						<Button variant="ghost" onClick={onClose} disabled={sending}>
							{t("ui.actions.cancel")}
						</Button>
						<Button
							variant="primary"
							loading={sending}
							disabled={description.trim() === ""}
							onClick={submit}
						>
							{t("portal.finance.dispute.submit")}
						</Button>
					</>
				)
			}
		>
			{done !== null ? (
				<p role="status" className="text-[13px] text-fg">
					{t("portal.finance.dispute.opened")}{" "}
					<Link
						to={`/tickets/${encodeURIComponent(done.ticketId)}`}
						className="font-semibold text-brand-ink underline-offset-2 hover:underline"
					>
						{t("portal.finance.dispute.openTicket", { number: done.ticketNumber })}
					</Link>
				</p>
			) : (
				<div className="flex flex-col gap-4">
					<Field label={t("portal.finance.dispute.reason")} required>
						{(control) => (
							<Select
								{...control}
								value={reason}
								onValueChange={(next) => setReason(next as StatementLineDisputeReason)}
								options={DISPUTE_REASONS.map((value) => ({
									value,
									label: t(`portal.finance.dispute.reasons.${value}`),
								}))}
							/>
						)}
					</Field>
					<Field
						label={t("portal.finance.dispute.description")}
						required
						{...(error?.field === "note" || error?.field === "description"
							? { error: error.message }
							: {})}
					>
						{(control) => (
							<Textarea
								{...control}
								rows={4}
								maxLength={1000}
								value={description}
								onChange={(event) => setDescription(event.target.value)}
							/>
						)}
					</Field>
					<TicketFiles api={api} storeId={storeId} files={files} onChange={setFiles} t={t} />
					{error !== null && error.field !== "note" && error.field !== "description" && (
						<p role="alert" className="text-[12.5px] text-danger-fg">
							{error.message}
						</p>
					)}
				</div>
			)}
		</Dialog>
	);
}
